import { memo, useEffect, useMemo, useRef, useState } from "react";
import "dropdown-menu-pattern/dist/main.css";
import PopUp from "./PopUp/PopUp";
 
export type DropdownOption = {
  id: number | string;
  label: string;
  value: string;
  iconPath?: string;
};
 
export type DropdownProps = {
  label?: string | JSX.Element; // 👈 label text
  options: DropdownOption[];
  selectedValue?: DropdownOption;
  placeholder?: string;
  onSelect: (value: DropdownOption) => void;
  buttonClassName?: string;
  menuClassName?: string;
  itemClassName?: string;
  popupMessage?: string;
  popupTitle?: string;
  popupPlacement?: string;
  disabled?: boolean;
  popupClassName?: string;
  buttonLabel?: string;
  helperText?: string;
};
 
// Constants for better maintainability
const CHEVRON_IMAGE_PATH = "/assets/images/chevrondown.svg";
const DISABLED_CHEVRON_IMAGE_PATH = "/assets/images/lockSolid.svg";
const CHECKMARK_IMAGE_PATH = "/assets/images/checkmark-outline.svg";
const DEFAULT_PLACEHOLDER = "Select an option";
const DEFAULT_POPUP_PLACEMENT = "top";
 
const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  selectedValue,
  placeholder = DEFAULT_PLACEHOLDER,
  onSelect,
  buttonClassName = "",
  menuClassName,
  itemClassName,
  popupMessage,
  popupTitle,
  popupPlacement = DEFAULT_POPUP_PLACEMENT,
  disabled = false,
  popupClassName = "",
  buttonLabel,
  helperText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
 
  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === selectedValue?.id),
    [options, selectedValue?.id],
  );
 
  const rootRef = useRef<HTMLDivElement>(null);
 
  useEffect(() => {
    // Use pointerdown so it triggers before focus changes; mousedown also works.
    const onPointerDown = (e: PointerEvent) => {
      const el = rootRef.current;
      if (!el) return;
 
      // If the click target is outside the dropdown root, close it.
      if (!el.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
 
    // Only attach when open to avoid unnecessary listeners.
    if (isOpen) {
      document.addEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
    }
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
    };
  }, [isOpen]);
 
  return (
    <div
      className={`ds-dropdown ds-dropdown--initialized max-w-none!`}
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="2"
      data-init-type="static"
      data-select-label="selected"
      data-list-type="option"
      ref={rootRef}
    >
      <div className="ds-dropdown__drop-menu">
        {/* Label */}
        {label && (
          <div className="flex">
            <label className="ds-dropdown__label">
              <span className="ds-dropdown__label-text"> {label}</span>
            </label>
            {popupMessage && (
              <PopUp
                headerText={popupTitle}
                message={popupMessage}
                placement={popupPlacement}
                className={popupClassName}
              />
            )}
          </div>
        )}
 
        <button
          type="button"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          className={`ds-dropdown__trigger-button  ${buttonClassName} ${disabled ? "opacity-50 cursor-not-allowed border-slate-500! bg-gray-100!" : ""}`}
          name="ds-dropdown__trigger-button-0"
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-labelledby="ds-dropdown__label-0 ds-dropdown__trigger-text-0"
          disabled={disabled}
        >
          {disabled && (
            <img
              className=" brightness-0!"
              src={DISABLED_CHEVRON_IMAGE_PATH}
              alt=""
              width={24}
              height={24}
            />
          )}
          <span
            className={`ds-dropdown__trigger-text ${disabled ? "text-slate-500!" : ""}`}
          >
            {" "}
            {!buttonLabel && selectedOption
              ? selectedOption.label
              : (buttonLabel ?? placeholder)}
          </span>
          <img
            className={`ds-dropdown__chevron filter ${disabled ? "filter! brightness-0!" : ""}`}
            src={CHEVRON_IMAGE_PATH}
            alt=""
            width={24}
            height={24}
          />
        </button>
        <ul
          className={`ds-dropdown__menu-list ${menuClassName} ${
            isOpen ? "-expanded" : ""
          }`}
        >
          {options.map((option, index) => (
            <li
              key={`${index}-${option.id}-${option.label}`}
              data-name={`${option.id}-${option.label}`}
              className={`p-0! drop-menu-list-op ds-dropdown__option ${
                selectedValue?.id === option.id ? "active" : ""
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
                className={`w-full text-left flex items-center py-3! pl-3! pr-4! ${option.iconPath ? "hover:filter! hover:brightness-0! hover:saturate-100! hover:invert!" : ""} ${itemClassName} `}
              >
                <img
                  className={`${option.iconPath ? "checkmark-img visible! opacity-100!" : "checkmark-img"}`}
                  alt=""
                  src={option.iconPath ?? CHECKMARK_IMAGE_PATH}
                />
                <span className="drop-menu-list-text ml-2!">
                  {" "}
                  {option.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {helperText && (
        <div className="text-xs mt-1! text-border-gray!">{helperText}</div>
      )}
    </div>
  );
};
 
// Memoize component to prevent unnecessary re-renders
export default memo(Dropdown);